import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { HStack, VStack, Text, Button } from '@chakra-ui/react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import {
  Mutation,
  Query,
  StudyGroup,
  Track,
  TrackTeacherAssignment,
} from '../../../../generated/graphqlModels';
import { fullNameUsernameOrEmail } from '../../../../helpers/username';
import { OptionType } from '../Unit/UnitPageComponents/ChakraReactSelect';
import {
  ASSIGN_TRACK_TO_STUDENT,
  DELETE_TRACK_ASSIGNMENT,
  STUDENTS_TRACK_ASSIGNMENTS,
} from '../Unit/queries';
import { client } from '../../../..';
import { trackAction } from '../../../../helpers/trackAction';

const animatedComponents = makeAnimated();

interface TrackAssignmentsProps {
  studyGroup?: StudyGroup;
  tracks?: Track[];
}

const TrackAssignments: React.FC<TrackAssignmentsProps> = (props) => {
  const { studyGroup, tracks } = props;

  const [allValues, setAllValues] = useState<OptionType[] | null>();

  const trackOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    tracks?.forEach((t) => {
      opts.push({ value: t.id, label: t.name });
    });
    return opts;
  }, [tracks]);

  const {
    data: dataTrackAssignments,
    loading,
  } = useQuery<Query>(STUDENTS_TRACK_ASSIGNMENTS, {
    variables: { studyGroupId: studyGroup?.id },
    skip: studyGroup == null || studyGroup.id == null,
  });

  const [createTrackAssignment] = useMutation<Mutation>(
    ASSIGN_TRACK_TO_STUDENT,
  );
  const [deleteTrackAssignment] = useMutation<Mutation>(
    DELETE_TRACK_ASSIGNMENT,
  );

  const [userTrackMap, userAssignmentMap] = useMemo(() => {
    const userTrackMap = new Map<string, string[]>();
    const userAssignmentMap = new Map<string, TrackTeacherAssignment[]>();
    if (dataTrackAssignments && dataTrackAssignments.trackTeacherAssignments) {
      dataTrackAssignments.trackTeacherAssignments.forEach((tTA) => {
        const userId = tTA.user.id;
        userTrackMap.set(userId, [
          ...new Set([...(userTrackMap.get(userId) || []), tTA.track.id]),
        ]);
        userAssignmentMap.set(userId, [
          ...new Set([...(userAssignmentMap.get(userId) || []), tTA]),
        ]);
      });
    }
    return [userTrackMap, userAssignmentMap];
  }, [dataTrackAssignments, studyGroup]);

  const studentValuesMap = useMemo(() => {
    const studentValuesMap = new Map<
      string,
      { value: string; label: string }[]
    >();
    if (studyGroup && studyGroup.students) {
      studyGroup.students.forEach((s) => {
        const trackIds = userTrackMap.get(s.pk) || [];
        studentValuesMap.set(
          s.pk,
          trackOptions.filter((tO) => trackIds.includes(tO.value)),
        );
      });
    }

    return studentValuesMap;
  }, [userTrackMap, studyGroup]);

  const addToAll = () => {
    if (studyGroup && allValues && allValues.length > 0) {
      studyGroup.students.forEach((s) => {
        // @ts-ignore
        const valueIds = allValues?.map((v) => v.value);
        const valuesToCreate = valueIds
          ? valueIds.filter(
              (id) => !(userTrackMap.get(s.pk) || []).includes(id),
            )
          : [];

        if (valuesToCreate) {
          valuesToCreate.forEach((v) => {
            createTrackAssignment({
              variables: {
                data: {
                  track: v,
                  studyGroup: studyGroup.id,
                  user: s.pk,
                },
              },
            });
            trackAction({
              action_type: 'assign_track',
              study_group: { pk: studyGroup.id },
              action_object: {
                pk: s.pk,
                type: 'user',
              },
              data: { trackId: v },
            });
          });
        }
        client.refetchQueries({
          include: [STUDENTS_TRACK_ASSIGNMENTS],
        });
        setTimeout(() => {
          client.refetchQueries({
            include: [STUDENTS_TRACK_ASSIGNMENTS],
          });
        }, 2000);
      });
    }
  };

  return (
    <VStack
      w="full"
      mb={16}
      flexGrow={1}
      maxW={['600px', '600px', '800px']}
      justify="center"
      alignItems="center"
    >
      {studyGroup && (
        <HStack mb={8}>
          <Select
            styles={{
              container: (provided, state) => ({
                ...provided,
                minWidth: '250px',
                marginLeft: '32px',
              }),
            }}
            isMulti
            isClearable
            placeholder={'Select learning paths'}
            options={trackOptions}
            components={animatedComponents}
            closeMenuOnSelect={false}
            onChange={(values) => {
              // @ts-expect-error
              setAllValues(values);
            }}
          />
          <Button colorScheme="blue" onClick={addToAll} isLoading={loading}>
            Add to all
          </Button>
        </HStack>
      )}
      {studyGroup &&
        studyGroup.students.map((s) => (
          <HStack
            px={2}
            w="full"
            key={s.id}
            justify="space-between"
            sx={{
              '&:nth-of-type(2n)': {
                background: '#f3f3f3',
              },
            }}
          >
            <Text>{fullNameUsernameOrEmail(s)}</Text>
            <Select
              isLoading={loading}
              styles={{
                container: (provided, state) => ({
                  ...provided,
                  minWidth: '250px',
                  marginLeft: '32px',
                }),
              }}
              isMulti
              isClearable
              placeholder={'Assign learning paths'}
              options={trackOptions}
              components={animatedComponents}
              closeMenuOnSelect={false}
              value={studentValuesMap.get(s.pk) || []}
              onChange={(values) => {
                const valueIds = values?.map((v: any) => v.value);
                const assignmentsToDelete = (
                  userAssignmentMap.get(s.pk) || []
                ).filter(
                  (tTA) => valueIds == null || !valueIds.includes(tTA.track.id),
                );
                assignmentsToDelete.forEach((tTA) => {
                  deleteTrackAssignment({
                    variables: { trackAssignmentId: tTA.id },
                  });
                  trackAction({
                    action_type: 'assign_track',
                    study_group: { pk: studyGroup.id },
                    action_object: {
                      pk: tTA.user.id,
                      type: 'user',
                    },
                    data: { trackId: tTA.track.id, delete: true },
                  });
                });
                const valuesToCreate = valueIds
                  ? valueIds.filter(
                      (id: any) => !(userTrackMap.get(s.pk) || []).includes(id),
                    )
                  : [];

                if (valuesToCreate) {
                  valuesToCreate.forEach((v: any) => {
                    createTrackAssignment({
                      variables: {
                        data: {
                          track: v,
                          studyGroup: studyGroup.id,
                          user: s.pk,
                        },
                      },
                    });
                    trackAction({
                      action_type: 'assign_track',
                      study_group: { pk: studyGroup.id },
                      action_object: {
                        pk: s.pk,
                        type: 'user',
                      },
                      data: { trackId: v },
                    });
                  });
                }
                client.refetchQueries({
                  include: [STUDENTS_TRACK_ASSIGNMENTS],
                });
                setTimeout(() => {
                  client.refetchQueries({
                    include: [STUDENTS_TRACK_ASSIGNMENTS],
                  });
                }, 2000);
              }}
            />
          </HStack>
        ))}
    </VStack>
  );
};

export default TrackAssignments;
export { TrackAssignments };
